import React from 'react';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { EmptyState, StatusPill } from '../../../components/app';
import { ProvidersTableProps } from '../types';
import { formatDay, formatNumber } from '../format';

/** Льготный период: комиссию начнут удерживать только с будущей даты. */
const isCommissionFree = (commissionFrom: string | null) =>
  Boolean(commissionFrom) && commissionFrom! > new Date().toISOString().slice(0, 10);

const percent = (value: number) => `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value)}%`;

const num = 'text-right tabular-nums';
const sub = 'block text-xs font-normal text-text-2';

/**
 * Сверка по провайдерам в Валюте провайдера: у Apple — доллары, под ними сумовый
 * эквивалент и Курс. Итоговая строка — в сумах.
 */
export function ProvidersTable({ providers, total, lastSettlement, isLoading, formatMoney }: ProvidersTableProps) {
  return (
    <Card className="overflow-hidden" aria-labelledby="cf-prov">
      <div className="flex flex-wrap items-center justify-between gap-3 px-[18px] py-4">
        <b id="cf-prov" className="font-display text-[17px] font-bold">
          По провайдерам
        </b>
        <span className="text-[13px] text-text-2">Суммы — в Валюте провайдера</span>
      </div>
      <Table className="min-w-[900px]">
        <TableHeader>
          <TableRow>
            <TableHead>Провайдер</TableHead>
            <TableHead className="text-right">Собрано</TableHead>
            <TableHead className="text-right">Ставка комиссии</TableHead>
            <TableHead className="text-right">Чистое</TableHead>
            <TableHead className="text-right">Поступило</TableHead>
            <TableHead className="text-right">
              Осталось получить
              <span className="block text-[11px] font-medium normal-case tracking-normal">за всё время</span>
            </TableHead>
            <TableHead>Последнее поступление</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 4 }, (_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 7 }, (__, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-full max-w-24" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : providers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>
                <EmptyState title="Провайдеров нет" description="Добавьте провайдера, чтобы вести сверку." />
              </TableCell>
            </TableRow>
          ) : (
            providers.map((p) => {
              const foreign = p.currency !== 'UZS';
              const free = isCommissionFree(p.commissionFrom);
              return (
                <TableRow key={p.providerId}>
                  <TableCell>
                    <span className="flex flex-col items-start gap-1">
                      <b>{p.name}</b>
                      <StatusPill size="sm" tone="neutral" className="h-[22px] text-[11px] text-text-2">
                        {p.source === 'manual' ? 'Начисления' : 'Из платежей'}
                        {foreign && ` · ${p.currency}`}
                      </StatusPill>
                    </span>
                  </TableCell>
                  <TableCell className={num}>
                    {formatMoney(p.gross, p.currency)}
                    {foreign && <span className={sub}>≈ {formatNumber(p.grossUzs)} сум</span>}
                  </TableCell>
                  <TableCell className={num}>
                    {free ? '0%' : percent(p.commissionPercent)}
                    {free && (
                      <span className={sub} title={`Ставка ${percent(p.commissionPercent)} с ${formatDay(p.commissionFrom)}`}>
                        Льготный период до {formatDay(p.commissionFrom)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={num}>{formatMoney(p.net, p.currency)}</TableCell>
                  <TableCell className={num}>{formatMoney(p.settled, p.currency)}</TableCell>
                  <TableCell className={`${num} font-display font-bold`}>
                    {formatMoney(p.outstanding, p.currency)}
                    {foreign && (
                      <span className={`${sub} font-sans`}>
                        {p.rate > 0
                          ? `≈ ${formatNumber(p.outstandingUzs)} сум · курс ${formatNumber(p.rate)}`
                          : 'Курс не задан'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-text-2 tabular-nums">{formatDay(lastSettlement[p.providerId])}</TableCell>
                </TableRow>
              );
            })
          )}
          {!isLoading && providers.length > 0 && (
            <TableRow className="border-border bg-surface-alt hover:bg-surface-alt">
              <TableHead scope="row" className="h-auto py-3 text-sm normal-case tracking-normal text-text">
                Итого, сум
              </TableHead>
              <TableCell className={`${num} font-bold`}>{formatNumber(total.gross)}</TableCell>
              <TableCell className="text-right text-text-2">—</TableCell>
              <TableCell className={`${num} font-bold`}>{formatNumber(total.net)}</TableCell>
              <TableCell className={`${num} font-bold`}>{formatNumber(total.settled)}</TableCell>
              <TableCell className={`${num} font-display font-extrabold`}>{formatNumber(total.outstanding)}</TableCell>
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
